'use client';

import type { Service } from '@ahm/api-wrappers-core';
import dayjs, { unix } from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export function useDetectBanHour() {
  const { selectedService, orderTime } = useBookingStore((state) => state);

  // Helper function to get current hour and minute
  const getCurrentTime = useCallback(() => {
    if (orderTime) {
      const time = unix(orderTime);
      return { hour: time.hour(), minute: time.minute() };
    }
    const now = dayjs();
    return { hour: now.hour(), minute: now.minute() };
  }, [orderTime]);

  // Helper function to check if time is in ban hours
  const isInBanHours = useCallback(
    (banHours: { from: number; to: number }[]) => {
      const { hour, minute } = getCurrentTime();
      return banHours.some(
        (banHour) =>
          banHour.from <= hour &&
          (banHour.to > hour || (banHour.to === hour && minute === 0))
      );
    },
    [getCurrentTime]
  );

  const banHourData = useMemo(() => {
    if (
      selectedService?.truck_ban?.enable &&
      selectedService?.truck_ban?.ban_hours?.length
    ) {
      const { hour, minute } = getCurrentTime();
      return selectedService.truck_ban.ban_hours.find(
        (banHour: { from: number; to: number }) =>
          banHour.from <= hour &&
          (banHour.to > hour || (banHour.to === hour && minute === 0))
      );
    }
    return undefined;
  }, [selectedService, getCurrentTime]);

  const checkServiceInBanFn = useCallback(
    (service: Service) => {
      const isBanEnabled = Boolean(
        service?.truck_ban?.enable && service?.truck_ban?.ban_hours?.length
      );

      return (
        isBanEnabled &&
        service.truck_ban &&
        isInBanHours(service.truck_ban.ban_hours)
      );
    },
    [isInBanHours]
  );

  return { banHourData, checkServiceInBanFn };
}
